import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { SyncRvqImmunizationResponse } from "./ResponseModels/SyncRvqImmunizationResponse";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";

interface SyncRvqImmunizationPopupProps {
    immunization: MedicalItemImmunization;
    onClose: () => void;
    left: number;
    top: number;
}

/**
 * The state for the {@link SyncRvqImmunizationPopup} component.
 */
class SyncRvqImmunizationState {
    /**
     * Whether the user clicked 'submit' and the immunization is currently being synced with the RVQ.
     */
    public isSyncing: boolean;

    /**
     * Whether the user authenticated with the RVQ after this component was opened, if needed to submit the immunization.
     */
    public authenticatedAfterOpen: boolean = false;
}

/**
 * A small popup with a button that syncs an immunization with the Quebec vaccination registry (RVQ),
 * if the user is authenticated with the RVQ and has permission to do so.
 */
class SyncRvqImmunizationPopup extends BaseReactComponent<SyncRvqImmunizationPopupProps, SyncRvqImmunizationState> {
    private width: number = 300;

    public async componentDidMount(): Promise<void> {
        this.setOverlay(
            "SyncRvqImmunizationPopupOverlay",
            {
                transparent: true,
                onClicked: () => this.props.onClose()
            }
        );

        // If the button is disabled because the user isn't authenticated with the RVQ, enable the
        // button if we can authenticate now.
        const justAuthenticated =
            !this.canSubmitToRvq()
            && !sessionStore.rvq.isAuthenticated
            && await sessionStore.rvq.tryRequireAuthentication(false)
            && this.canSubmitToRvq();

        if (justAuthenticated)
            this.setState({ authenticatedAfterOpen: true });
    }

    public render(): JSX.Element {
        return (
            <div
                className="notes-modal"
                onClick={e => e.stopPropagation()}
                style={{
                    position: "absolute",
                    left: this.left,
                    top: this.props.top,
                    border: "solid 1px #ccc",
                    width: `${this.width}px`,
                    zIndex: 1500
                }}
            >

                <div style={{ padding: "15px 20px" }}>
                    <h4 className="uppercase">{cacheStore.GetString("StatusDLabel")}</h4>
                    <span>{this.text}</span>
                </div>

                <div
                    className="notes-content-footer text-right"
                    style={{ padding: "10px 10px" }}
                >
                    <button
                        className="btn btn-default"
                        onClick={() => this.props.onClose()}
                    >
                        {cacheStore.GetString("CloseLabel")}
                    </button>

                    {!this.rvqSyncUpToDate && (
                        <button
                            className="btn btn-primary push-left-5"
                            onClick={() => this.onSubmitClick()}
                            disabled={this.state?.isSyncing || !this.canSubmitToRvq()}
                        >
                            {cacheStore.GetString("NoteSubmitLabel")}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    private async syncImmunization(immunization: MedicalItemImmunization): Promise<ClientResponse<SyncRvqImmunizationResponse>> {
        return await Client.postAsync<SyncRvqImmunizationResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_SyncImmunization,
            {
                ImmunizationMedicalItemId: immunization.Id,
                IsMedicalItemInvalid: immunization.DisplayAsInvalid
            }
        );
    }

    private get rvqSyncUpToDate(): boolean {
        return QuebecVaccinationRegistryHelper.isQuebecRegistryVaccinationSynced(this.immunization);
    }

    private get hasBeenSynced(): boolean {
        return this.immunization.SubmittedToQuebecRegistryOn != null;
    }

    private get isInvalid(): boolean {
        return this.props.immunization.DisplayAsInvalid;
    }

    private onSubmitClick(): void {
        this.mutateState(
            s => s.isSyncing = true,
            () => this.sync()
        );
    }

    private async sync(): Promise<void> {
        const response = await this.syncImmunization(this.immunization);

        if (response.isSuccess) {
            const immunizationToUpdate = new MedicalItemImmunization({ ...this.immunization });
            if (response.data?.SubmittedToQuebecRegistryOn)
                immunizationToUpdate.SubmittedToQuebecRegistryOn = response.data?.SubmittedToQuebecRegistryOn;

            immunizationToUpdate.QuebecRegistryId = response.data?.Immunization?.Id;

            if (response.data?.DeletionDate)
                immunizationToUpdate.QuebecRegistryDeletionDate = response.data?.DeletionDate;

            sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateImmunizationMedicalItem, { immunization: immunizationToUpdate });
        }
        else
            sessionStore.showPopup(response.errors);

        this.props.onClose();
    }

    private get immunization(): MedicalItemImmunization {
        return this.props.immunization;
    }

    private get text(): string {
        let vocabKey;

        if (this.state?.isSyncing) vocabKey = "QuebecVaccinationRegistry_SyncStatus_Syncing";
        else if (this.rvqSyncUpToDate) vocabKey = "QuebecVaccinationRegistry_SyncStatus_Submitted";
        else if (!this.hasBeenSynced) vocabKey = "QuebecVaccinationRegistry_SyncStatus_NotSubmitted";
        else if (this.isInvalid) vocabKey = "QuebecVaccinationRegistry_SyncStatus_InvalidatedAfterSubmission";
        else vocabKey = "QuebecVaccinationRegistry_SyncStatus_EditedAfterSubmission";

        return cacheStore.GetString(vocabKey);
    }

    private get left(): number {
        return this.overflowsWindow
            ? window.innerWidth - this.width
            : this.props.left;
    }

    private get overflowsWindow(): boolean {
        return (this.props.left + this.width) > window.innerWidth;
    }

    /**
     * Get whether the user can submit this immunization to the RVQ.
     */
    private canSubmitToRvq(): boolean {
        return sessionStore.rvq.canSubmitToRvq(this.immunization, false);
    }
}

export { SyncRvqImmunizationPopup, SyncRvqImmunizationPopupProps };
